// src/ui/screens/focus.ts
// FOCUS MODE — WHAT THE ATHLETE LOOKS AT BETWEEN BALLS.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THIS SCREEN RENDERS A DECISION IT DID NOT MAKE
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `src/practice/focus-service.ts` on the backend is 1,600 lines whose entire job is to decide
// whether anything may be said about a ball, and what. It starts at SILENT and has to be argued up
// to SPEAK through ten gates in a fixed order: mode, retention block, focus membership, the metric
// registry, rollout flags, the target band, MEASUREMENT NOISE, severity, latency, then the fade and
// the per-block cap. This file's whole contribution is to put that answer on a phone without
// changing it.
//
// So the rules here are subtractive, and they are the point of the lane:
//
//   · NEVER INVENT A CUE. The only sentences an athlete sees come out of `CUE_CATALOGUE` on the
//     server, in `text.en` and `text.hi`, and they are rendered byte for byte. Not translated, not
//     shortened, not re-worded, not "made friendlier". Those strings were written to be honest
//     about what a phone camera can actually see, and every one of them is run through the prose
//     guard at module load on the backend — a cue that borrowed Hawk-Eye's credibility would stop
//     the server from starting. Editing one here would route around that guard entirely.
//   · NEVER SOFTEN A REFUSAL. `decideCue` answers with either a cue or a reason it stayed quiet.
//     BOTH ARE RENDERED, and the quiet one is NOT an error. "Nothing measurable to say about that
//     ball" is the common, correct, designed outcome — it is what the system does when the
//     deviation is smaller than the tracker's own wobble, and dressing it up as a failure would
//     teach a coach to treat honest silence as a bug to be worked around.
//   · NEVER ADD ENCOURAGEMENT THE SERVER DID NOT SEND. No "nice one", no streaks, no praise for a
//     ball nobody measured. A system that congratulates a fourteen-year-old for a two-millimetre
//     shift inside its own error bar has taught them to chase an artefact of the tracker.
//   · COUNTS, NEVER PERCENTAGES. `bowl_target_hit`'s own metric card lists "small denominators:
//     3 of 6 is not 50%" as an error mode. Six balls is a small denominator. The block summary says
//     "4 of 6" and this file contains no division. The load-time guard below refuses a per-cent
//     sign in any string this file authors.
//   · NO MEDICAL OR PSYCHOLOGICAL LANGUAGE, anywhere. Also guarded at load.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHERE THE NUMBERS COME FROM — AND WHY THERE ARE NONE TODAY
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The cue endpoint needs OBSERVATIONS: what was actually read off this ball, with the two
// timestamps that let the server apply the latency rule. There is no on-device inference in this
// app — capture is the phone's own camera and a file upload, and the analysis happens later,
// elsewhere. So this build has NO measurement pipeline, and this file will not manufacture one.
//
// `setMeasurementSource()` is the seam the measurement lane plugs into when it exists. Until then
// nothing is registered, every ball is posted with an empty observation list, and the server
// answers `NO_OBSERVATIONS` — "nothing was observed on this ball." That sentence is the server's,
// it is displayed as itself, and it is the truth.
//
// THE BALL IS STILL POSTED, rather than short-circuited on a local flag. That is deliberate and it
// is the same rule `capture.ts` follows for storage: the authority on what may be said is the
// server, and a client that decides locally will drift from it. A demo mode that filled in
// plausible readings would be exactly the defect this whole lane exists to prevent — praise
// manufactured on the phone, indistinguishable on screen from praise the evidence supports.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHAT IS DELIBERATELY NOT SHOWN
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `SessionReport.withheld` is coach-facing. The backend says so in the type: "Never rendered on the
// athlete screen — that is the whole point of the caps." Two things that went well, three to work
// on, one drill. Rendering the withheld list here would put the wall of findings back on the screen
// the caps exist to protect, one field at a time. It is fetched, and it is dropped.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalLine, refusalView } from '../refusal-view';
import { getState } from '../state';
import { isRefusal, request, type ApiResult } from '../../api/client';
import type { Refusal } from '../../api/refusal';
import { getSession } from '../../api/practice';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import { STORE, idbGet, idbPut } from '../../store/idb';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SERVER'S SHAPES, transcribed from src/practice/focus-service.ts and routes/focus.ts.
 *
 * Transcribed, not guessed — the file names are above so the next person can diff them. Nothing
 * here is optional-by-accident: `text` is required on a cue because a cue with no words is not a
 * cue, and `reason` is required on a decision because a decision with no reason cannot be shown.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export const FEEDBACK_MODES = ['GUIDED', 'LIGHT', 'QUIET'] as const;
export type FeedbackMode = (typeof FEEDBACK_MODES)[number];

/**
 * A MIRROR of `MAX_CUES_PER_BLOCK` in focus-service.ts, used for ONE thing: telling the athlete,
 * before they choose, how much this mode will say. It is not a control and nothing here consults
 * it when deciding to render a cue — the cap is enforced on the server, which is the only place
 * that can see the whole block. If these numbers ever disagree with the backend the screen is
 * merely wrong about a label; it cannot let an extra cue through, because it never had the vote.
 */
export const MODE_CUE_CAP: Readonly<Record<FeedbackMode, number>> = Object.freeze({
  GUIDED: 2, LIGHT: 1, QUIET: 0,
});

/** English and Hindi are both required, exactly as the server's `LocalisedText` requires them. */
export interface LocalisedText {
  readonly en: string;
  readonly hi: string;
  readonly [lang: string]: string;
}

export interface FocusAreaOption {
  readonly key: string;
  readonly discipline: string;
  readonly area: string;
  readonly label: LocalisedText;
  readonly metrics: readonly string[];
}

/** `practiceLabEnabled()`'s decision, reported by the options route. */
export interface LabFlagDecision {
  readonly enabled?: boolean;
  readonly reason?: string;
  readonly flag?: string | null;
  readonly message?: string;
}

export interface FocusOptions {
  readonly max_priorities: number;
  readonly modes: readonly string[];
  readonly block_size: number;
  readonly areas: readonly FocusAreaOption[];
  readonly lab: LabFlagDecision | boolean | null;
  readonly baselines_wired: boolean;
}

export interface TargetBand {
  readonly low: number;
  readonly high: number;
  readonly unit?: string | null;
}

interface ObservationBase {
  readonly metric_id: string;
  /** 1-based index of the ball within the session. */
  readonly ball: number;
  /** Session clock, ms. When the ball happened. */
  readonly observed_at_ms: number;
  /** Session clock, ms. When inference finished for this ball. */
  readonly inference_ready_at_ms: number;
}

export interface NumericObservation extends ObservationBase {
  readonly kind: 'NUMERIC';
  readonly value: number;
  /** COACHING INTENT — the target the drill was set to. Never evidence about accuracy. */
  readonly band: TargetBand;
}

export interface CategoricalObservation extends ObservationBase {
  readonly kind: 'CATEGORICAL';
  readonly value: string;
  readonly acceptable: readonly string[];
}

export type FocusObservation = NumericObservation | CategoricalObservation;

export interface CueCandidateTrace {
  readonly metric_id: string;
  readonly outcome: string;
  readonly detail: string;
  readonly severity: string;
  readonly direction: string;
  readonly defer: string;
}

export interface Cue {
  readonly metric_id: string;
  readonly metric_label: string;
  readonly focus_area: string;
  readonly focus_key: string;
  readonly cue_key: string;
  /** THE WORDS. The server's, verbatim, in both languages. */
  readonly text: LocalisedText;
  readonly ball: number;
  readonly block: number;
  readonly direction: string;
  readonly severity: string;
  readonly inference_latency_ms: number;
  readonly age_at_decision_ms: number;
  readonly measurement_class: string;
  readonly not_tracking_grade: boolean;
  readonly disclaimer: string;
}

export interface CueDecision {
  readonly speak: boolean;
  readonly cue: Cue | null;
  readonly reason: string;
  /** The server's own sentence about why. Rendered verbatim, never replaced. */
  readonly detail: string;
  readonly defer: string;
  readonly ball: number;
  readonly block: number;
  readonly retention_block: boolean;
  readonly mode: string;
  readonly min_gap_balls: number;
  readonly cues_this_block: number;
  readonly considered: readonly CueCandidateTrace[];
  readonly deferred: readonly string[];
}

export interface CueResponse extends CueDecision {
  readonly focus: { readonly areas: readonly string[]; readonly metrics: readonly string[] };
  readonly enabled_metrics: readonly string[];
  readonly noise_models: number;
  readonly baselines_wired: boolean;
}

export interface BlockSummary {
  readonly block: number;
  readonly ball_from: number;
  readonly ball_to: number;
  readonly balls_observed: number;
  readonly on_target: number | null;
  readonly lines: readonly LocalisedText[];
  readonly retention_now: boolean;
  readonly retention_next: boolean;
  readonly deferred_metrics: readonly string[];
  readonly disclaimer: string;
}

export interface ReportItem {
  readonly metric_id: string;
  readonly focus_key: string | null;
  readonly text: LocalisedText;
  readonly measurement_class: string;
}

export interface ChangeRuling {
  readonly metric_id: string;
  readonly verdict: string;
  readonly threshold: number | null;
  readonly observed_change: number | null;
  readonly reason: string;
  readonly downgraded: boolean;
}

export interface BeforeAfterItem {
  readonly metric_id: string;
  readonly metric_label: string;
  readonly before: number;
  readonly after: number;
  readonly unit: string | null;
  readonly threshold: number;
  readonly ruling: ChangeRuling;
}

export interface NoChangeItem {
  readonly metric_id: string;
  /** 'NO MEASURABLE CHANGE YET', in both languages. CONTENT, not an empty state. */
  readonly text: LocalisedText;
  readonly why: LocalisedText;
  readonly ruling: ChangeRuling;
}

export interface DrillCandidate {
  readonly drill_id: string | null;
  readonly drill_key: string;
  readonly drill_version: number;
  readonly title: LocalisedText;
  readonly rank: number;
  readonly contraindicated?: boolean;
}

export interface SessionReport {
  readonly went_well: readonly ReportItem[];
  readonly work_on: readonly ReportItem[];
  readonly next_drill: DrillCandidate | null;
  readonly next_drill_reason: string | null;
  readonly before_after: readonly BeforeAfterItem[];
  readonly no_measurable_change: readonly NoChangeItem[];
  readonly headings: {
    readonly went_well: LocalisedText;
    readonly work_on: LocalisedText;
    readonly next_drill: LocalisedText;
    readonly before_after: LocalisedText;
  };
  readonly caps: {
    readonly went_well_max: number;
    readonly work_on_max: number;
    readonly next_drill: number;
    readonly before_after_max: number;
  };
  /** COACH-FACING. Deliberately never read by this file. See the header block. */
  readonly withheld?: unknown;
  readonly disclaimer: string;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ROUTES — src/practice/routes/focus.ts
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export function focusOptions(discipline?: string): Promise<ApiResult<FocusOptions>> {
  return request(`${NS}/focus/options`, { query: { discipline: discipline ?? null } });
}

export interface CueRequestBody {
  readonly mode: FeedbackMode;
  readonly focus_areas: readonly string[];
  readonly ball: number;
  readonly observations: readonly FocusObservation[];
  readonly spoken_cue_balls: readonly number[];
  readonly now_ms: number;
}

export function postCue(sessionId: string, body: CueRequestBody): Promise<ApiResult<CueResponse>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/focus/cue`, { method: 'POST', body });
}

export function postBlockSummary(
  sessionId: string,
  body: {
    readonly block: number;
    readonly focus_areas: readonly string[];
    readonly observations: readonly FocusObservation[];
    readonly deferred_metrics: readonly string[];
  },
): Promise<ApiResult<BlockSummary>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/focus/block-summary`, { method: 'POST', body });
}

export function postSessionSummary(
  sessionId: string,
  body: { readonly focus_areas: readonly string[] },
): Promise<ApiResult<SessionReport>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/focus/summary`, { method: 'POST', body });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE MEASUREMENT SEAM — the one place a reading may enter, and it is empty today
 *
 * A source hands back what was actually READ off a ball. It is not a generator, and the screen has
 * no fallback that fills in for a missing one: with nothing registered, every ball goes to the
 * server with zero observations and the server says so. The `name` is displayed, so a number on
 * this screen is never anonymous — a coach can always see which pipeline claims to have measured
 * it, which is the first question to ask when a cue looks wrong.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface BallReadingRequest {
  readonly session_id: string;
  readonly ball: number;
  /** The metrics behind this session's chosen priorities, as the server listed them. */
  readonly metrics: readonly string[];
  /** The session clock at the moment the ball was recorded, ms. */
  readonly now_ms: number;
}

export interface MeasurementSource {
  /** Shown on screen. Name the pipeline, not the vendor's promise. */
  readonly name: string;
  observationsForBall(req: BallReadingRequest): Promise<readonly FocusObservation[]>;
}

let source: MeasurementSource | null = null;

/** Register the pipeline that measures balls on this device. `null` uninstalls it. */
export function setMeasurementSource(s: MeasurementSource | null): void {
  source = s;
}

export function measurementSourceName(): string | null {
  return source ? source.name : null;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * EVERY WORD THIS FILE AUTHORS, IN ONE PLACE, GUARDED AT LOAD
 *
 * The backend keeps its athlete-facing strings in `CUE_CATALOGUE` / `SESSION_TEXT` and runs the
 * whole catalogue through `assertProseHonest` at import, deliberately not as a test, because "a
 * test can be skipped". This is the client-side half of the same posture. It covers ONLY the chrome
 * this file writes — server text is guarded where it is authored and is never edited here.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

const CHROME = Object.freeze({
  title: 'Focus Mode',
  no_athlete: 'No athlete is selected. Choose one on the Coach screen first — an athlete id is only '
    + 'ever used after the server handed it to us.',
  no_session: 'No practice session is open. Focus Mode runs inside a session, because every decision '
    + 'it renders is made against that session\'s own capture conditions.',
  loading: 'Asking the server what may be said in this session…',
  options_failed: 'The list of priorities could not be read, so there is nothing to choose from. The '
    + 'server\'s answer is below, unedited.',

  intro: 'Between balls, this screen shows exactly one thing: what the server decided may be said '
    + 'about the ball just bowled. When it decided to stay quiet, it says that instead, with its '
    + 'reason. Silence here is an outcome, not a failure.',

  priorities_title: 'Your priorities for this session',
  priorities_help: 'One or two. The server checks them against this session\'s discipline and refuses '
    + 'a priority that does not belong to it.',
  priorities_locked: 'Priorities are fixed once the first ball is recorded. The balls already logged '
    + 'were measured against these, and changing them mid-session would leave the counts describing '
    + 'two different sessions.',
  priorities_none: 'Nothing chosen yet.',
  discipline_unknown: 'This session\'s discipline could not be read, so every priority is offered. The '
    + 'server still checks the choice and will refuse one that does not belong to this session.',

  mode_title: 'How much is said',
  mode_help: 'The cap is the most this mode will say in a six-ball block. It is applied on the '
    + 'server, which is the only place that can see the whole block.',
  mode_quiet_note: 'QUIET stops live cues. Block summaries still run.',

  live_title: 'The ball just bowled',
  record_ball: 'Record a ball',
  recording: 'Asking the server…',
  need_focus_first: 'Choose a priority and a mode first.',
  no_source: 'No measurement pipeline is registered in this build, so each ball is sent with nothing '
    + 'measured on it and the server answers accordingly. Nothing on this screen is filled in locally.',
  source_named: 'Readings for each ball come from: ',
  no_decision_yet: 'No ball recorded yet in this session.',

  silent_note: 'This is the server\'s decision and its own words. It is not an error.',
  considered_summary: 'What was considered on this ball',
  held_for_summary: 'the block summary',

  log_title: 'This session, ball by ball',
  log_empty: 'Nothing recorded yet.',

  block_title: 'Every six balls',
  block_empty: 'No block finished yet. A summary is asked for at the end of each six balls, in every '
    + 'mode including QUIET.',

  summary_title: 'After the session',
  summary_button: 'Ask for the post-session summary',
  summary_empty: 'Not asked for yet.',
  summary_findings_none: 'The server returned no items under this heading.',
  summary_no_findings_source: 'This build sends no findings with the request, because nothing on the '
    + 'phone computes them — they come from the coach review lane. The server therefore has none to '
    + 'rank, and says so by returning none.',
  no_change_intro: 'Where the change is smaller than this set-up can measure, the server says so '
    + 'rather than claiming an improvement:',

  baselines_off: 'The baselines service is not wired on this deployment. Without it there is no '
    + 'measurement-noise estimate for any metric, so no numeric reading can be told apart from '
    + 'tracker wobble, and every before/after comes back as no measurable change yet. That is the '
    + 'correct posture for a lane with no history, not a fault to work around.',
  ledger_not_durable: 'This session\'s ball log could not be written to this device\'s storage. It '
    + 'still works, but a reload will lose the record of which balls were cued — and the server '
    + 'spaces cues using that record.',

  counts_only: 'Counts, never a share: six balls is too small a denominator to turn into one.',
} as const);

/**
 * The short label per silence reason. THE LABEL IS OURS; THE SENTENCE UNDER IT IS ALWAYS THEIRS —
 * the same division `refusal-view.ts` draws, and for the same reason. A reason this table does not
 * know is rendered as its raw code rather than swallowed, so a new server outcome shows up as
 * itself instead of disappearing into a default.
 */
const SILENCE_LABEL: Readonly<Record<string, string>> = Object.freeze({
  NO_OBSERVATIONS: 'Nothing was measured on that ball',
  MODE_QUIET: 'QUIET — no live cues',
  RETENTION_BLOCK: 'Silent block',
  NOT_A_FOCUS_METRIC: 'Outside this session’s priorities',
  REGISTRY_REFUSED: 'Not measurable under this capture',
  NOT_ATHLETE_VISIBLE: 'Not spoken to an athlete',
  FLAG_OFF: 'Not switched on for this rollout',
  NO_CUE_TEXT: 'No cue written for that metric',
  IN_BAND: 'Inside the target',
  NOT_ENOUGH_CONSECUTIVE: 'Not repeated yet',
  NO_NOISE_MODEL: 'No measurement-noise estimate',
  BREACH_WITHIN_NOISE: 'Smaller than the measurement noise',
  BREACH_NOT_MEANINGFUL: 'Under this mode’s threshold',
  LATENCY_BUDGET_MISSED: 'Too late to be live — held',
  CUE_STALE: 'Too old to speak',
  FADED_FREQUENCY: 'Spacing — held',
  BLOCK_CUE_CAP: 'Block cue cap reached — held',
});

/* ── THE LOAD-TIME GUARD. Not a test: a test can be skipped, and this must not be skippable. ──
 *
 * The patterns are a client-side restatement of the backend's `TRACKING_CLAIM`
 * (src/video_assessment/estimate.ts) plus this lane's own two bans — no per-cent, no clinical
 * vocabulary. It checks ONLY the strings above. Server text passes through untouched precisely
 * because it was already checked where it was written, and re-checking it here would be this file
 * arriving at an opinion about the server's words. */
const ACCURACY_CLAIM = /hawk-?eye|\bdrs\b|snicko|ball[-\s]?tracking|tracking-grade|frame-accurate|broadcast-grade/i;
const PER_CENT = /%|\bper\s?cent|\bpercent/i;
const CLINICAL = /\b(diagnos|injur|therap|rehab|concussion|anxiet|anxious|depress|psycholog|psychiatr|trauma|medicat|clinical|mental health|pain)\w*/i;

(function assertChromeIsHonest(): void {
  const check = (where: string, value: string): void => {
    if (ACCURACY_CLAIM.test(value)) {
      throw new Error(`[focus] ${where} claims an accuracy this app cannot support: "${value}"`);
    }
    if (PER_CENT.test(value)) {
      throw new Error(`[focus] ${where} expresses a share instead of a count: "${value}"`);
    }
    if (CLINICAL.test(value)) {
      throw new Error(`[focus] ${where} uses medical or psychological language: "${value}"`);
    }
  };
  for (const [k, v] of Object.entries(CHROME)) check(`CHROME.${k}`, v);
  for (const [k, v] of Object.entries(SILENCE_LABEL)) check(`SILENCE_LABEL.${k}`, v);
})();

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SESSION LEDGER — durable, because the fade and the cap are computed from it
 *
 * `spoken_cue_balls` is not bookkeeping. The server reads it to apply BOTH the per-block cap and
 * the faded-frequency spacing, and it is the client that has to remember it. If a reload dropped
 * it, ball 7 would look like the first ball of the session and the cap would silently reset — the
 * app would start talking more the more often it crashed. So it goes to IndexedDB after every ball.
 *
 * THE CLOCK. The server wants a monotonic session clock. `performance.now()` restarts at zero when
 * the tab is torn down, which would make ball 40 look one millisecond old and quietly re-qualify
 * stale feedback as live. An epoch anchor stored with the ledger survives the reload, so the
 * elapsed figure stays honest across it. Its weakness is the opposite one and worth naming: if the
 * device clock is stepped mid-session the elapsed value jumps, and a jump forward can only make a
 * cue look OLDER — which the server answers with silence. It fails towards saying less.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface FocusLedger {
  session_id: string;
  anchor_epoch_ms: number;
  mode: FeedbackMode | null;
  focus_areas: string[];
  ball: number;
  spoken_cue_balls: number[];
  observations: FocusObservation[];
  decisions: CueResponse[];
  blocks: BlockSummary[];
  deferred_by_block: Record<string, string[]>;
  report: SessionReport | null;
}

const ledgerKey = (sessionId: string): string => `focus:${sessionId}`;

function freshLedger(sessionId: string): FocusLedger {
  return {
    session_id: sessionId,
    anchor_epoch_ms: Date.now(),
    mode: null,
    focus_areas: [],
    ball: 0,
    spoken_cue_balls: [],
    observations: [],
    decisions: [],
    blocks: [],
    deferred_by_block: {},
    report: null,
  };
}

export async function loadLedger(sessionId: string): Promise<FocusLedger> {
  const stored = await idbGet<FocusLedger>(STORE.kv, ledgerKey(sessionId)).catch(() => undefined);
  if (stored && stored.session_id === sessionId && typeof stored.anchor_epoch_ms === 'number') return stored;
  return freshLedger(sessionId);
}

/**
 * Exported alongside `loadLedger` so the ledger is inspectable and settable from outside — which is
 * what makes "the cap survives a reload" a claim a suite can actually drive, rather than one that
 * has to be believed. Returns whether the write landed; the caller says so on screen when it did
 * not, instead of pretending the record is safe.
 */
export async function saveLedger(l: FocusLedger): Promise<boolean> {
  try {
    await idbPut(STORE.kv, l, ledgerKey(l.session_id));
    return true;
  } catch {
    return false;
  }
}

/** A ledger for a session that has not started. Exported for the same reason as `saveLedger`. */
export function newLedger(sessionId: string): FocusLedger {
  return freshLedger(sessionId);
}

const clockOf = (l: FocusLedger): number => Date.now() - l.anchor_epoch_ms;

/**
 * Which disciplines this session may pick from.
 *
 * `null` means "we could not tell", and that is rendered as not-knowing rather than resolved into a
 * guess. The backend's own `disciplinesOf` defaults an unrecognised value to BOWLING; copying that
 * default here would show a bowling-only picker to a session whose discipline we failed to read,
 * and the athlete would never learn why their batting priorities were missing.
 */
function disciplinesOf(athleteFocus: string | null): readonly string[] | null {
  if (!athleteFocus) return null;
  const f = athleteFocus.trim().toUpperCase();
  if (f === 'BOTH') return ['BOWLING', 'BATTING'];
  if (f === 'BATTING') return ['BATTING'];
  if (f === 'BOWLING') return ['BOWLING'];
  return null;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * RENDERING
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A localised string pair, both languages, both verbatim.
 *
 * `textContent` all the way down (see ui/dom.ts) — and the two are separate nodes with a `lang`
 * attribute rather than one concatenated line, so a screen reader and a TTS voice can each take the
 * one they can pronounce. `text.en` is required by the server's own type, so there is no
 * "fall back to the other language" branch here; a missing Hindi string would be a server defect
 * and would show as a missing line rather than as English pretending to be Hindi.
 */
function bilingual(text: LocalisedText, cls: string): HTMLElement {
  return el('div', { class: cls },
    el('p', { class: 'cue-en', lang: 'en' }, text.en),
    typeof text.hi === 'string' && text.hi !== '' && el('p', { class: 'cue-hi', lang: 'hi' }, text.hi));
}

/** The cue itself. Every word below the labels came off the wire. */
function cueView(c: Cue): HTMLElement {
  return el('div', { class: 'row-item cue cue-spoken' },
    bilingual(c.text, 'cue-text'),
    el('div', { class: 'counts' },
      field('Ball', c.ball),
      field('Block', c.block),
      field('Priority', c.focus_key),
      field('Measure', c.metric_label),
      field('Direction', c.direction),
      field('Severity', c.severity),
      field('Ready after', `${c.inference_latency_ms} ms`)),
    el('p', { class: 'small' }, c.disclaimer));
}

/**
 * THE SILENCE. Rendered as an outcome, and specifically NOT as a refusal.
 *
 * No `role="alert"`, no `refusal-*` class, no warning colour, no retry button. A `decideCue` that
 * declined to speak answered 200 and answered correctly; showing it in the same red box as a 503
 * would teach every coach who ever reads this screen that the honest default state of the product
 * is broken.
 */
function silenceView(d: CueDecision): HTMLElement {
  const label = SILENCE_LABEL[d.reason] ?? d.reason;
  return el('div', { class: 'row-item cue cue-silent' },
    el('p', { class: 'cue-silent-headline' }, label),
    // ── THE SERVER'S OWN SENTENCE. Verbatim.
    el('p', { class: 'cue-silent-detail' }, d.detail),
    el('div', { class: 'counts' },
      field('Ball', d.ball),
      field('Block', d.block),
      field('Mode', d.mode),
      field('Cues so far in this block', d.cues_this_block),
      field('Spacing', `${d.min_gap_balls} balls`),
      d.retention_block ? field('Silent retention block', 'yes') : null,
      d.defer === 'BLOCK_SUMMARY' ? field('Held for', CHROME.held_for_summary) : null),
    d.deferred.length > 0 && field('Held metrics', [...new Set(d.deferred)].join(', ')),
    consideredView(d),
    el('p', { class: 'small' }, CHROME.silent_note));
}

/** The audit trail for "why was it quiet?" — which is the question a coach actually asks. */
function consideredView(d: CueDecision): HTMLElement | false {
  if (!d.considered.length) return false;
  return el('details', { class: 'cue-considered' },
    el('summary', {}, `${CHROME.considered_summary} (${d.considered.length})`),
    ...d.considered.map((t) => el('div', { class: 'cue-trace' },
      el('p', { class: 'mono small' }, `${t.metric_id} · ${t.outcome}`),
      el('p', { class: 'small' }, t.detail))));
}

function decisionView(d: CueDecision): HTMLElement {
  return d.speak && d.cue ? cueView(d.cue) : silenceView(d);
}

function blockView(b: BlockSummary): HTMLElement {
  return el('div', { class: 'row-item block-summary' },
    ...b.lines.map((line) => bilingual(line, 'block-line')),
    el('div', { class: 'counts' },
      field('Block', b.block),
      field('Balls', `${b.ball_from}–${b.ball_to}`),
      // COUNTS. "4 of 6", never a share of it — see the header block.
      field('On target', b.on_target === null ? 'nothing measurable this block' : `${b.on_target} of ${b.balls_observed}`),
      b.retention_now ? field('This block', 'silent') : null,
      b.retention_next ? field('Next block', 'silent') : null),
    b.deferred_metrics.length > 0 && field('Held from live cues', b.deferred_metrics.join(', ')),
    el('p', { class: 'small' }, b.disclaimer));
}

function reportItemsView(heading: LocalisedText, items: readonly ReportItem[]): HTMLElement {
  return el('div', { class: 'report-group' },
    bilingual(heading, 'report-heading'),
    items.length === 0
      ? empty(CHROME.summary_findings_none)
      : el('div', { class: 'list' }, ...items.map((i) => el('div', { class: 'row-item' },
        bilingual(i.text, 'report-item-text'),
        el('p', { class: 'mono small' }, `${i.metric_id} · ${i.measurement_class}`)))));
}

/**
 * NO MEASURABLE CHANGE YET, as a first-class answer.
 *
 * It gets the same weight as a before/after — a heading, the sentence, and the explanation the
 * server sent with it — because it IS the answer, not the absence of one. Rendering it as an empty
 * state would say "we have nothing", when what the system actually established is "the change is
 * smaller than this set-up can resolve", which is a measurement result and a useful one.
 */
function noChangeView(items: readonly NoChangeItem[]): HTMLElement {
  return el('div', { class: 'report-group no-measurable-change' },
    el('p', { class: 'note' }, CHROME.no_change_intro),
    el('div', { class: 'list' }, ...items.map((i) => el('div', { class: 'row-item no-change-item' },
      bilingual(i.text, 'no-change-text'),
      bilingual(i.why, 'no-change-why'),
      el('p', { class: 'mono small' }, `${i.metric_id} · ${i.ruling.verdict}`),
      el('p', { class: 'small' }, i.ruling.reason)))));
}

function beforeAfterView(heading: LocalisedText, items: readonly BeforeAfterItem[]): HTMLElement {
  return el('div', { class: 'report-group' },
    bilingual(heading, 'report-heading'),
    ...items.map((i) => el('div', { class: 'row-item before-after' },
      el('p', { class: 'progress-what' }, i.metric_label),
      el('div', { class: 'counts' },
        field('Before', `${i.before}${i.unit ? ` ${i.unit}` : ''}`),
        field('After', `${i.after}${i.unit ? ` ${i.unit}` : ''}`),
        field('Had to beat', `${i.threshold}${i.unit ? ` ${i.unit}` : ''}`)),
      el('p', { class: 'small' }, i.ruling.reason),
      i.ruling.downgraded && el('p', { class: 'warn' },
        'The baselines lane claimed more than its own numbers support; the claim was reduced.'))));
}

/** The post-session card. `withheld` is NOT read — see the header block. */
function reportView(r: SessionReport): HTMLElement {
  return el('div', { class: 'session-report' },
    reportItemsView(r.headings.went_well, r.went_well),
    reportItemsView(r.headings.work_on, r.work_on),
    el('div', { class: 'report-group' },
      bilingual(r.headings.next_drill, 'report-heading'),
      r.next_drill
        ? el('div', { class: 'row-item' },
          bilingual(r.next_drill.title, 'drill-title'),
          el('p', { class: 'mono small' }, `${r.next_drill.drill_key} v${r.next_drill.drill_version}`))
        : el('p', { class: 'note' }, r.next_drill_reason ?? '')),
    r.before_after.length > 0 && beforeAfterView(r.headings.before_after, r.before_after),
    r.no_measurable_change.length > 0 && noChangeView(r.no_measurable_change),
    el('div', { class: 'counts' },
      field('Went well, at most', r.caps.went_well_max),
      field('Work on, at most', r.caps.work_on_max),
      field('Drills', r.caps.next_drill),
      field('Before/after, at most', r.caps.before_after_max)),
    el('p', { class: 'small' }, r.disclaimer));
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export async function focusScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();
  if (!st.athlete) {
    mount(root, section(CHROME.title, empty(CHROME.no_athlete)));
    return;
  }
  if (!st.sessionId) {
    mount(root, section(CHROME.title, empty(CHROME.no_session)));
    return;
  }
  const sessionId = st.sessionId;

  mount(root, section(CHROME.title, spinner(CHROME.loading)));

  const optionsRes = await focusOptions();
  if (isRefusal(optionsRes)) {
    mount(root, section(CHROME.title,
      el('p', { class: 'note' }, CHROME.options_failed),
      refusalView(optionsRes, { onRetry: rerender })));
    return;
  }
  const options = optionsRes.value;

  // The session's own discipline decides which priorities are legal. A refusal here is NOT fatal:
  // the picker widens instead of narrowing, and the SERVER still refuses an illegal choice — which
  // is the answer that counts. Failing the whole screen because one read was forbidden would hide
  // a working surface behind a permission problem it does not have.
  const sessionRes = await getSession(sessionId);
  const sessionRefusal: Refusal | null = isRefusal(sessionRes) ? sessionRes : null;
  const athleteFocus = isRefusal(sessionRes)
    ? null
    : (typeof sessionRes.value.session['athlete_focus'] === 'string'
      ? (sessionRes.value.session['athlete_focus'] as string)
      : null);
  const allowed = disciplinesOf(athleteFocus);

  const ledger = await loadLedger(sessionId);
  const modes = (options.modes.filter((m): m is FeedbackMode =>
    (FEEDBACK_MODES as readonly string[]).includes(m)));

  let busy = false;
  let durable = true;
  let lastRefusal: Refusal | null = null;

  const selectedMetrics = (): readonly string[] => {
    const out: string[] = [];
    for (const key of ledger.focus_areas) {
      const area = options.areas.find((a) => a.key === key);
      for (const m of area?.metrics ?? []) if (!out.includes(m)) out.push(m);
    }
    return out;
  };

  const persist = async (): Promise<void> => {
    durable = await saveLedger(ledger);
  };

  /* ── ONE BALL. The whole live loop is these thirty lines. ─────────────────────────────────── */
  const recordBall = async (): Promise<void> => {
    const mode = ledger.mode;
    if (busy || !mode || ledger.focus_areas.length === 0) return;
    busy = true;
    paint();
    try {
      const ball = ledger.ball + 1;
      const nowMs = clockOf(ledger);

      // The ONLY entry point for a reading. With no source registered this is an empty array and
      // stays one; there is no branch below that fills it in.
      const fresh = source
        ? await source.observationsForBall({
          session_id: sessionId, ball, metrics: selectedMetrics(), now_ms: nowMs,
        }).catch((): readonly FocusObservation[] => [])
        : [];

      // Only observations for THIS ball are kept, whatever a source hands back. A source that
      // returned a reading labelled ball 3 while ball 9 is being recorded would rewrite history the
      // server uses for its consecutive-miss run, and the server has no way to notice.
      //
      // NOTHING IS COMMITTED TO THE LEDGER UNTIL THE SERVER HAS ANSWERED. A refused request — a
      // stopped lab, a dead network — must leave the session exactly as it was, or a retry would
      // record the same ball twice and the counts on this screen would drift up every time the
      // signal dropped.
      const attempt = [...ledger.observations, ...fresh.filter((o) => o.ball === ball)];

      // The full history goes up, not just this ball: `consecutiveOffTarget` on the server counts
      // how many balls IN A ROW a categorical metric read off-target, and it can only do that with
      // the earlier balls in hand.
      const res = await postCue(sessionId, {
        mode,
        focus_areas: ledger.focus_areas,
        ball,
        observations: attempt,
        spoken_cue_balls: ledger.spoken_cue_balls,
        now_ms: nowMs,
      });

      if (isRefusal(res)) {
        lastRefusal = res;
      } else {
        lastRefusal = null;
        ledger.observations = attempt;
        ledger.ball = ball;
        ledger.decisions.push(res.value);
        if (res.value.speak) ledger.spoken_cue_balls.push(ball);
        const key = String(res.value.block);
        const held = new Set([...(ledger.deferred_by_block[key] ?? []), ...res.value.deferred]);
        ledger.deferred_by_block[key] = [...held];
        if (options.block_size > 0 && ball % options.block_size === 0) {
          await requestBlockSummary(res.value.block);
        }
      }
      await persist();
    } finally {
      busy = false;
      paint();
    }
  };

  const requestBlockSummary = async (block: number): Promise<void> => {
    const res = await postBlockSummary(sessionId, {
      block,
      focus_areas: ledger.focus_areas,
      observations: ledger.observations,
      deferred_metrics: ledger.deferred_by_block[String(block)] ?? [],
    });
    if (isRefusal(res)) { lastRefusal = res; return; }
    ledger.blocks = [...ledger.blocks.filter((b) => b.block !== block), res.value].sort((a, b) => a.block - b.block);
  };

  const requestReport = async (): Promise<void> => {
    if (busy || ledger.focus_areas.length === 0) return;
    busy = true;
    paint();
    try {
      const res = await postSessionSummary(sessionId, { focus_areas: ledger.focus_areas });
      if (isRefusal(res)) { lastRefusal = res; return; }
      lastRefusal = null;
      ledger.report = res.value;
      await persist();
    } finally {
      busy = false;
      paint();
    }
  };

  const toggleArea = (key: string): void => {
    if (ledger.ball > 0) return;
    const at = ledger.focus_areas.indexOf(key);
    if (at >= 0) ledger.focus_areas.splice(at, 1);
    else if (ledger.focus_areas.length < options.max_priorities) ledger.focus_areas.push(key);
    void persist();
    paint();
  };

  const setMode = (m: FeedbackMode): void => {
    ledger.mode = m;
    void persist();
    paint();
  };

  /* ── PANELS ──────────────────────────────────────────────────────────────────────────────── */

  const headerPanel = (): HTMLElement => {
    const lab = options.lab;
    const labOff = lab !== null && typeof lab === 'object' && lab.enabled === false;
    return section(CHROME.title,
      el('p', { class: 'note' }, CHROME.intro),
      el('div', { class: 'counts' },
        field('Session', sessionId),
        field('Discipline', athleteFocus ?? 'not readable'),
        field('Balls recorded', ledger.ball),
        field('Cues given', ledger.spoken_cue_balls.length),
        field('Block size', options.block_size)),
      // The server's own sentence about its own flag, carried unedited.
      labOff && el('p', { class: 'warn' }, (lab as LabFlagDecision).message ?? ''),
      options.baselines_wired === false && el('p', { class: 'note' }, CHROME.baselines_off),
      !durable && el('p', { class: 'warn' }, CHROME.ledger_not_durable),
      sessionRefusal && refusalLine(sessionRefusal));
  };

  const prioritiesPanel = (): HTMLElement => {
    const locked = ledger.ball > 0;
    const visible = allowed
      ? options.areas.filter((a) => allowed.includes(a.discipline))
      : options.areas;
    return section(CHROME.priorities_title,
      el('p', { class: 'note' }, CHROME.priorities_help),
      !allowed && el('p', { class: 'note' }, CHROME.discipline_unknown),
      locked && el('p', { class: 'note' }, CHROME.priorities_locked),
      field('Chosen', ledger.focus_areas.length ? ledger.focus_areas.join(', ') : CHROME.priorities_none),
      field('At most', options.max_priorities),
      el('div', { class: 'list' }, ...visible.map((a) => {
        const on = ledger.focus_areas.includes(a.key);
        return el('div', { class: on ? 'row-item row-selected' : 'row-item' },
          // The area's own label, from the server, in both languages.
          bilingual(a.label, 'focus-area-label'),
          el('p', { class: 'mono small' }, a.key),
          el('p', { class: 'mono small' }, a.metrics.join(', ')),
          !locked && el('button', {
            class: on ? 'btn' : 'btn btn-quiet',
            onclick: () => toggleArea(a.key),
          }, on ? 'Chosen — remove' : 'Choose'));
      })));
  };

  const modePanel = (): HTMLElement => section(CHROME.mode_title,
    el('p', { class: 'note' }, CHROME.mode_help),
    field('Mode', ledger.mode ?? '—'),
    el('div', { class: 'row' }, ...modes.map((m) => el('button', {
      class: ledger.mode === m ? 'btn btn-primary' : 'btn',
      onclick: () => setMode(m),
    }, `${m} · at most ${MODE_CUE_CAP[m]} in ${options.block_size}`))),
    ledger.mode === 'QUIET' && el('p', { class: 'note' }, CHROME.mode_quiet_note));

  const livePanel = (): HTMLElement => {
    const latest = ledger.decisions.length ? ledger.decisions[ledger.decisions.length - 1] : undefined;
    const ready = ledger.mode !== null && ledger.focus_areas.length > 0;
    return section(CHROME.live_title,
      source
        ? el('p', { class: 'note' }, `${CHROME.source_named}${source.name}`)
        : el('p', { class: 'note' }, CHROME.no_source),
      !ready && el('p', { class: 'note' }, CHROME.need_focus_first),
      el('div', { class: 'row' },
        el('button', {
          class: 'btn btn-primary',
          disabled: busy || !ready,
          onclick: () => { void recordBall(); },
        }, busy ? CHROME.recording : CHROME.record_ball)),
      // An HTTP refusal — a stopped lab, a 403, a dead network — IS an error surface, and renders
      // through the shared view so it looks like every other refusal in the app. A quiet DECISION
      // never comes through here.
      lastRefusal && refusalView(lastRefusal, { onRetry: () => { void recordBall(); } }),
      latest ? decisionView(latest) : empty(CHROME.no_decision_yet));
  };

  const logPanel = (): HTMLElement => section(CHROME.log_title,
    ledger.decisions.length === 0
      ? empty(CHROME.log_empty)
      : el('div', { class: 'list cue-log' }, ...[...ledger.decisions].reverse().map(decisionView)));

  const blocksPanel = (): HTMLElement => section(CHROME.block_title,
    el('p', { class: 'small' }, CHROME.counts_only),
    ledger.blocks.length === 0
      ? empty(CHROME.block_empty)
      : el('div', { class: 'list block-list' }, ...ledger.blocks.map(blockView)));

  const summaryPanel = (): HTMLElement => section(CHROME.summary_title,
    el('p', { class: 'note' }, CHROME.summary_no_findings_source),
    el('div', { class: 'row' },
      el('button', {
        class: 'btn',
        disabled: busy || ledger.focus_areas.length === 0,
        onclick: () => { void requestReport(); },
      }, CHROME.summary_button)),
    ledger.report ? reportView(ledger.report) : empty(CHROME.summary_empty));

  function paint(): void {
    mount(root,
      headerPanel(),
      prioritiesPanel(),
      modePanel(),
      livePanel(),
      logPanel(),
      blocksPanel(),
      summaryPanel());
  }

  paint();
}
